// Sound: Web Audio, driven by client/audio/manifest.json (id → files without extension, category, gain dB, loop,
// pitch jitter). Buses: music · amb · sfx · vo → master → a limiter (a DynamicsCompressor used as a limiter only:
// Chrome's compressor adds make-up gain otherwise — Wobble's trap). Rules from docs/sound/SOUND_BRIEF.md: one bed at a
// time (flip law: beds change only on a scene change, TOWN↔TENSION crossfade inside a round), the Crier ducks the bed
// −4 dB, never two Crier lines at once, repeated cues come from a shuffle bag with ±2 semitones.
// The platform's mute (CrazyGames settings) always wins over the player's own sliders.

type Cat = "music" | "amb" | "sfx" | "vo";
interface Entry { files: string[]; cat: Cat; gain?: number; loop?: boolean; pitch?: number; }

const BASE = "./audio/";
let ctx: AudioContext | null = null;
let master: GainNode, limiter: DynamicsCompressorNode;
const bus = {} as Record<Cat, GainNode>;
let manifest: Record<string, Entry> = {};
const buffers = new Map<string, Promise<AudioBuffer | null>>();
const bags = new Map<string, number[]>();
let fmt = "ogg";
const vol = { music: 0.5, sfx: 0.8, vo: 0.9, all: 1 };
let platformMuted = false, userMuted = false;
const db = (d: number) => Math.pow(10, d / 20);

export function init(man: Record<string, Entry>) {
  manifest = man;
  const a = document.createElement("audio");
  fmt = a.canPlayType('audio/ogg; codecs="vorbis"') ? "ogg" : "mp3";
  try { const s = JSON.parse(localStorage.getItem("tnt.vol") || "{}"); Object.assign(vol, s); userMuted = !!s.muted; } catch {}
}

/** must run inside a user gesture (iOS) — the first click/tap/key */
export function unlock() {
  if (!ctx) {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext; if (!AC) return;
    ctx = new AC() as AudioContext;
    limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -3; limiter.knee.value = 0; limiter.ratio.value = 20; limiter.attack.value = 0.002; limiter.release.value = 0.12;
    master = ctx.createGain(); master.connect(limiter); limiter.connect(ctx.destination);
    for (const c of ["music", "amb", "sfx", "vo"] as Cat[]) { bus[c] = ctx.createGain(); bus[c].connect(master); }
    applyLevels();
    if (pendingBed !== undefined) { const b = pendingBed; pendingBed = undefined; music(b); }
    if (pendingAmb) { pendingAmb = false; ambience(true); }
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
}
export const ready = () => !!ctx && ctx.state === "running";

function applyLevels() {
  if (!ctx) return;
  const on = !(platformMuted || userMuted);
  master.gain.setTargetAtTime(on ? 0.85 * vol.all : 0, ctx.currentTime, 0.05);
  bus.music.gain.setTargetAtTime(vol.music * duckLevel, ctx.currentTime, 0.08);
  bus.amb.gain.setTargetAtTime(vol.sfx * 0.9, ctx.currentTime, 0.08);
  bus.sfx.gain.setTargetAtTime(vol.sfx, ctx.currentTime, 0.05);
  bus.vo.gain.setTargetAtTime(vol.vo, ctx.currentTime, 0.05);
}
export function setVolume(k: "music" | "sfx" | "vo" | "all", v: number) { vol[k] = Math.max(0, Math.min(1, v)); save(); applyLevels(); }
export function getVolume(k: "music" | "sfx" | "vo" | "all") { return vol[k]; }
export function setMuted(m: boolean) { userMuted = m; save(); applyLevels(); }
export const isMuted = () => userMuted;
export function setPlatformMuted(m: boolean) { platformMuted = m; applyLevels(); }
function save() { try { localStorage.setItem("tnt.vol", JSON.stringify({ ...vol, muted: userMuted })); } catch {} }

function load(file: string): Promise<AudioBuffer | null> {
  let p = buffers.get(file);
  if (!p) {
    p = fetch(`${BASE}${file}.${fmt}`).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(r.status)))
      .then((b) => new Promise<AudioBuffer>((ok, bad) => ctx!.decodeAudioData(b, ok, bad)))
      .catch((e) => { console.warn("[audio]", file, e); return null; });
    buffers.set(file, p);
  }
  return p;
}
/** decode a set of cues ahead of time (call after unlock) */
export function preload(ids: string[]) { if (!ctx) return; for (const id of ids) for (const f of manifest[id]?.files || []) load(f); }

function nextFile(id: string): string | null {
  const e = manifest[id]; if (!e || !e.files.length) return null;
  let bag = bags.get(id);
  if (!bag || !bag.length) { bag = e.files.map((_, i) => i).sort(() => Math.random() - 0.5); bags.set(id, bag); }
  return e.files[bag.pop()!];
}

export interface PlayOpts { gain?: number; dist?: number; pitch?: number; onEnd?: () => void; }
/** a one-shot. `dist` (m from the camera focus) applies a gentle rolloff: full inside 6 m, −12 dB at 24 m */
export async function play(id: string, o: PlayOpts = {}): Promise<AudioBufferSourceNode | null> {
  if (!ctx) return null;
  const e = manifest[id]; const f = nextFile(id); if (!e || !f) return null;
  const buf = await load(f); if (!buf || !ctx) return null;
  const src = ctx.createBufferSource(); src.buffer = buf;
  const jitter = e.pitch ?? 0;
  const semis = (o.pitch ?? 0) + (jitter ? (Math.random() * 2 - 1) * jitter : 0);
  src.playbackRate.value = Math.pow(2, semis / 12);
  const g = ctx.createGain();
  let gain = db(e.gain ?? 0) * (o.gain ?? 1);
  if (o.dist !== undefined) gain *= o.dist <= 6 ? 1 : Math.max(0.25, 1 - (o.dist - 6) / 24);
  g.gain.value = gain;
  src.connect(g); g.connect(bus[e.cat]);
  src.onended = () => { try { g.disconnect(); } catch {} o.onEnd?.(); };
  src.start();
  return src;
}

// ---- the Crier: one line at a time (a queue), the bed ducks under him
let duckLevel = 1;
let voBusy = false; const voQueue: string[] = [];
export function say(id: string) {
  if (voBusy) { if (voQueue.length < 2) voQueue.push(id); return; }
  voBusy = true; duck(0.63);
  play(id, { onEnd: () => { voBusy = false; duck(1); const n = voQueue.shift(); if (n) say(n); } }).then((s) => { if (!s) { voBusy = false; duck(1); } });
}
function duck(level: number) { duckLevel = level; if (ctx) bus.music.gain.setTargetAtTime(vol.music * level, ctx.currentTime, level < 1 ? 0.04 : 0.25); }

// ---- beds: one at a time; crossfade inside a scene (town ↔ tension), cut on a scene change
interface Bed { id: string; src: AudioBufferSourceNode; g: GainNode; t0: number; loopStart: number; loopLen: number; }
/** beds that crossfade inside a scene enter on the outgoing bed's bar line (docs/sound/SOUND_LOG.md: town and tension share
 *  key and tempo; a bar = 2.124 s at the measured 112.99 BPM) with an equal-power fade */
const BAR: Record<string, number> = { bed_town: 60 / 112.99 * 4, bed_tension: 60 / 112.99 * 4 };
const XFADE_PAIRS = new Set(["bed_town>bed_tension", "bed_tension>bed_town"]);
let bed: Bed | null = null;
let wanted: string | null | undefined;
let pendingBed: string | null | undefined;
let pendingAmb = false;
export async function music(id: string | null, fade = 1.2) {
  if (!ctx) { pendingBed = id; return; }          // before the first gesture: remember it, unlock() starts it
  if (bed?.id === id || wanted === id) return;
  wanted = id;
  const f = id ? nextFile(id) : null;
  const buf = f ? await load(f) : null;
  if (!ctx || wanted !== id) return;
  const old = bed; bed = null;
  const now = ctx.currentTime;
  const musical = !!(old && id && XFADE_PAIRS.has(`${old.id}>${id}`) && buf);
  // when: the next bar line of the outgoing bed (musical pairs), else now
  let when = now;
  if (musical && old) { const bar = BAR[old.id]; const pos = (now - old.t0) % old.loopLen; when = now + (bar - (pos % bar)) % bar; if (when - now < 0.05) when += bar; }
  const XF = musical ? 4 : fade;
  if (old) {
    if (musical) { const c = new Float32Array(32).map((_, i) => Math.cos((i / 31) * Math.PI / 2) * old.g.gain.value); old.g.gain.cancelScheduledValues(when); old.g.gain.setValueCurveAtTime(c, when, XF); }
    else old.g.gain.setTargetAtTime(0, now, fade / 4);
    const stopAt = (when - now + XF) * 1000 + 600; setTimeout(() => { try { old.src.stop(); } catch {} }, stopAt);
  }
  if (!id || !buf) return;
  const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
  const e = manifest[id] as Entry & { loopStart?: number; loopEnd?: number };
  const ls = e.loopStart ?? 0, le = e.loopEnd ?? buf.duration;
  src.loopStart = ls; src.loopEnd = le;
  const g = ctx.createGain(); g.gain.value = 0; src.connect(g); g.connect(bus.music);
  const target = db(e.gain ?? 0);
  if (musical) g.gain.setValueCurveAtTime(new Float32Array(32).map((_, i) => Math.sin((i / 31) * Math.PI / 2) * target), when, XF);
  else g.gain.setTargetAtTime(target, now, fade / 4);
  src.start(when, ls);                      // enter on bar 1 of the loop (the file's 0.5 s lead-in is decoder slack)
  bed = { id, src, g, t0: when, loopStart: ls, loopLen: le - ls };
}
let amb: { src: AudioBufferSourceNode; g: GainNode } | null = null;
export async function ambience(on: boolean) {
  if (!ctx) { pendingAmb = on; return; }
  if (!on) { if (amb) { const a = amb; amb = null; a.g.gain.setTargetAtTime(0, ctx.currentTime, 0.3); setTimeout(() => { try { a.src.stop(); } catch {} }, 1500); } return; }
  if (amb) return;
  const f = nextFile("crowd_murmur"); if (!f) return; const buf = await load(f); if (!buf || !ctx || amb) return;
  const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
  const am = manifest.crowd_murmur as Entry & { loopStart?: number; loopEnd?: number };
  if (am?.loopStart !== undefined) { src.loopStart = am.loopStart; src.loopEnd = am.loopEnd ?? buf.duration; }
  const g = ctx.createGain(); g.gain.value = 0; src.connect(g); g.connect(bus.amb);
  g.gain.setTargetAtTime(db(manifest.crowd_murmur?.gain ?? 0), ctx.currentTime, 0.5); src.start();
  amb = { src, g };
}
document.addEventListener("visibilitychange", () => { if (!ctx) return; if (document.hidden) ctx.suspend().catch(() => {}); else ctx.resume().catch(() => {}); });
